'use client';

import { useTranslation } from 'react-i18next';

import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { knownMyUrls } from '@sola/my-sdk/constants.ts';

import {
  AccountSettingsEmailPhone,
  AccountSettingsInformation,
  AccountSettingsMailingAddress,
  AccountSettingsProfile,
  AccountSettingsUsernamePassword,
  SettingDivider,
} from '../../molecules/account-settings-section/account-settings-section.tsx';
import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { AccountSettingsSkeleton } from './account-settings-skeleton.tsx';
import { useAccountSettingsLogic } from './account-settings.logic.ts';

export const AccountSettings = () => {
  const { t } = useTranslation(['my-settings', 'my']);

  const {
    supporter,
    supporterAddress,
    preferredUsername,
    showSkeleton,
    hasError,
    reviewAccountInfo,
    nonPrimaryContacts,
  } = useAccountSettingsLogic();

  return (
    <div className="flex flex-col gap-comfy-lg md:gap-comfy-xl mb-comfy-lg md:mb-comfy-xl max-w-md">
      <PrimerError keysToAlert={['details', 'addressDetails']} />
      {hasError && (
        <Alert
          variant="negative"
          title={t('my-settings:account.error.title')}
          message={t('my-settings:account.error.message')}
          action={
            <a href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </a>
          }
        />
      )}
      {reviewAccountInfo && (
        <Alert
          variant="warning"
          title={t('my-settings:account.reviewInfo.title')}
          message={t('my-settings:account.reviewInfo.message')}
        />
      )}
      {!showSkeleton && supporter && supporterAddress && (
        <>
          <AccountSettingsInformation supporter={supporter} />
          <SettingDivider />
          <AccountSettingsProfile supporter={supporter} />
          {/* TODO: Should show an error message in the section if we cannot retrieve username */}
          {preferredUsername && (
            <>
              <SettingDivider />
              <AccountSettingsUsernamePassword username={preferredUsername} />
            </>
          )}
          <SettingDivider />
          <AccountSettingsMailingAddress addressDetails={supporterAddress} />
          <SettingDivider />
          <AccountSettingsEmailPhone supporter={supporter} />
          {nonPrimaryContacts.length > 0 && (
            <Alert
              variant="neutral"
              title={t('my-settings:account.additionalContacts', {
                count: nonPrimaryContacts.length,
              })}
              message={t('my-settings:account.additionalContactsText', {
                count: nonPrimaryContacts.length,
              })}
              action={
                <a href={knownMyUrls.settingsContact}>
                  {t('my-settings:account.contactLink')}
                </a>
              }
            />
          )}
        </>
      )}
      {showSkeleton && <AccountSettingsSkeleton />}
    </div>
  );
};
